import { kybStyles } from "../kybStyles";
import { kybService } from "@services/apis/Kyb";
import { KybLinkRecord } from "@services/models/Kyb/GetKybLinks";
import React, { useRef, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";
import KybInlineEditor from "./KybInlineEditor";

interface MerchantNameCellRendererProps {
  data: KybLinkRecord;
  jwt: string | null;
  onUpdated: (record: KybLinkRecord) => void;
}

const MerchantNameCellRenderer: React.FC<MerchantNameCellRendererProps> = ({
  data,
  jwt,
  onUpdated,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const editRef = useRef<HTMLButtonElement>(null);
  const { handleSuccess } = useSnackbarMessage();
  const currentName = data.kyb_data?.merchantName || "";
  const link = `https://kyb.tylt.money/${data.id}${jwt ? `?token=${encodeURIComponent(jwt)}` : ""}`;

  const closeEditor = () => {
    setIsEditing(false);
    requestAnimationFrame(() => editRef.current?.focus());
  };

  if (isEditing) {
    return (
      <KybInlineEditor
        initialValue={currentName}
        label="Merchant name"
        onCancel={closeEditor}
        onSave={async (name) => {
          const merged = { ...(data.kyb_data ?? {}), merchantName: name };
          await kybService.saveKybData(
            merged,
            Boolean(data.is_submitted),
            data.id,
          );
          onUpdated({ ...data, kyb_data: merged });
          handleSuccess({ message: "Merchant name updated." });
          closeEditor();
        }}
      />
    );
  }

  return (
    <div className={kybStyles.cell}>
      <a
        className={`${kybStyles.focus} block flex-1 min-w-0 truncate text-[10px] leading-[1.5] no-underline hover:text-text-navbar-active hover:underline hover:underline-offset-[3px] ${currentName ? "text-text-base font-medium" : "text-text-subtle font-normal"}`}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        title={`${currentName || "Unnamed merchant"} · Open KYB link in a new tab`}
      >
        {currentName || "Unnamed merchant"}
      </a>
      <button
        ref={editRef}
        type="button"
        className={kybStyles.editIconButton}
        onClick={() => setIsEditing(true)}
        title="Edit merchant name"
        aria-label={`Edit merchant name for ${currentName || "unnamed merchant"}`}
      >
        <span className="material-symbols-outlined" aria-hidden="true">
          edit
        </span>
      </button>
    </div>
  );
};

export default MerchantNameCellRenderer;
